import * as React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type {
  SearchExplainBaseResult,
  SearchExplainQueryMetrics,
} from 'bundles/search-common/components/internal-search-explainer/types';

type SearchExplainContextValue = {
  hasPermission: boolean;
  enableSearchExplain: boolean;
  toggleSearchExplain: () => void;
  queryMetrics: SearchExplainQueryMetrics | null;
  metricsLoading: boolean;
  metricsError?: Error;
  baseResult: SearchExplainBaseResult | null;
  setBaseResult: (baseResult: SearchExplainBaseResult) => void;
  clearBaseResult: () => void;
};

type SearchExplainProviderValue = Omit<SearchExplainContextValue, 'baseResult' | 'setBaseResult' | 'clearBaseResult'>;

const SearchExplainContext = createContext<SearchExplainContextValue | undefined>(undefined);

type SearchExplainProviderProps = {
  children: React.ReactNode;
  value: SearchExplainProviderValue;
};

export const SearchExplainProvider = ({ children, value }: SearchExplainProviderProps) => {
  const [baseResult, setBaseResult] = useState<SearchExplainBaseResult | null>(null);

  const clearBaseResult = useCallback<() => void>(() => setBaseResult(null), []);

  // Pinned base is only meaningful while explain mode is on
  useEffect(() => {
    if (!value.enableSearchExplain) {
      setBaseResult(null);
    }
  }, [value.enableSearchExplain]);

  const mergedValue = useMemo<SearchExplainContextValue>(
    () => ({ ...value, baseResult, setBaseResult, clearBaseResult }),
    [value, baseResult, setBaseResult, clearBaseResult]
  );

  return <SearchExplainContext.Provider value={mergedValue}>{children}</SearchExplainContext.Provider>;
};

export const useSearchExplain = () => {
  const context = useContext(SearchExplainContext);
  if (context === undefined) {
    throw new Error('useSearchExplain must be used within a SearchExplainProvider');
  }
  return context;
};

export default SearchExplainContext;
